import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import CustomModal from '../../../components/ui/modal/modal.component';
import axios from '../../../api/axios';
import { CONTRACTS_AUTOMATION_BULK_UPDATE_ENDPOINT, SUCCESS_SNACKBAR, ERROR_SNACKBAR } from '../../../consts';
import { ContractAutomationItem } from '../../../types/contracts';
import { CounterPriorityProviderValue } from '../../../types/tenantSettings';

const PROVIDER_OPTIONS: { value: CounterPriorityProviderValue; label: string }[] = [
  { value: 'U', label: 'User' },
  { value: 'E', label: 'Email report' },
  { value: 'P', label: 'PDF' },
];

interface CounterPriorityConflictsModalProps {
  opened: boolean;
  setOpened: (value: boolean) => void;
  contracts: ContractAutomationItem[];
}

export const CounterPriorityConflictsModal = ({
  opened,
  setOpened,
  contracts,
}: CounterPriorityConflictsModalProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [values, setValues] = useState<Record<number, CounterPriorityProviderValue>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (opened) {
      setValues(
        contracts.reduce((acc: Record<number, CounterPriorityProviderValue>, contract) => {
          if (contract.counter_priority_provider) {
            acc[contract.id] = contract.counter_priority_provider;
          }
          return acc;
        }, {})
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, contracts]);

  const handleSave = async () => {
    const payload = contracts
      .filter((contract) => values[contract.id] !== contract.counter_priority_provider)
      .map((contract) => ({
        id: contract.id,
        counter_priority_provider: values[contract.id],
      }));

    if (payload.length === 0) {
      setOpened(false);
      return;
    }

    setSaving(true);
    try {
      await axios.patch(CONTRACTS_AUTOMATION_BULK_UPDATE_ENDPOINT, payload);
      enqueueSnackbar(t('Contracts were successfully updated.'), SUCCESS_SNACKBAR);
      setOpened(false);
    } catch (error) {
      enqueueSnackbar(t('There was an error, please try again.'), ERROR_SNACKBAR);
    } finally {
      setSaving(false);
    }
  };

  return (
    <CustomModal
      opened={opened}
      setOpened={setOpened}
      title={t('Contracts with a different counter priority provider')}
      width={560}
      content={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info">
            {t(
              'These contracts already have their own equipment counter priority provider, different from the new tenant default. Change them below, or leave them as they are.'
            )}
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: 360, overflowY: 'auto' }}>
            {contracts.map((contract) => (
              <Box
                key={contract.id}
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {[contract.number, contract.client?.name].filter(Boolean).join(' - ')}
                  </Typography>
                  {contract.internal_name && (
                    <Typography variant="body2" color="text.secondary">
                      {contract.internal_name}
                    </Typography>
                  )}
                </Box>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <Select
                    value={values[contract.id] || contract.counter_priority_provider || ''}
                    onChange={(event: SelectChangeEvent) =>
                      setValues((prev) => ({
                        ...prev,
                        [contract.id]: event.target.value as CounterPriorityProviderValue,
                      }))
                    }
                  >
                    {PROVIDER_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {t(option.label)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={() => setOpened(false)}>
              {t('Skip')}
            </Button>
            <Button variant="contained" color="success" disabled={saving} onClick={handleSave}>
              {saving ? t('Saving...') : t('Save changes')}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};
